import { CronExpressionParser } from "cron-parser";

/**
 * Compute the next due time from a cron schedule and last crawled time.
 * Returns null if no schedule is set.
 */
export function computeNextDue(
  crawlSchedule: string,
  lastCrawledAt: string | null,
): Date | null {
  try {
    const baseDate = lastCrawledAt ? new Date(lastCrawledAt) : new Date();
    const interval = CronExpressionParser.parse(crawlSchedule, {
      currentDate: baseDate,
    });
    return interval.next().toDate();
  } catch {
    return null;
  }
}

/**
 * Format a date as a relative time string.
 * Past dates: "just now", "5m ago", "3h ago", "12d ago", "Apr 7"
 * Future dates: "in 4h", "in 2d", "Apr 7"
 *
 * Falls back to absolute date for anything 30+ days out so relative
 * readings don't become meaningless ("57d ago" is less readable than "Feb 12").
 */
export function formatRelativeTime(
  date: Date | string | null | undefined,
): string {
  if (!date) return "";
  const d = typeof date === "string" ? new Date(date) : date;
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const absDiff = Math.abs(diffMs);

  const minutes = Math.floor(absDiff / 60_000);
  const hours = Math.floor(absDiff / 3_600_000);
  const days = Math.floor(absDiff / 86_400_000);

  if (minutes < 1) return "just now";

  let label: string | null = null;
  if (minutes < 60) label = `${minutes}m`;
  else if (hours < 24) label = `${hours}h`;
  else if (days < 30) label = `${days}d`;

  if (label) {
    return diffMs > 0 ? `in ${label}` : `${label} ago`;
  }

  // 30+ days out — show absolute date. Include year when it differs.
  const includeYear = d.getFullYear() !== now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(includeYear ? { year: "numeric" } : {}),
  });
}
